import { getPaneElements } from '../../../util/chartPanes'
import type Chart from '../chart'
import { DrawingTypes } from './drawing.interface'

/**
 * Touch-enabled charts currently mounted. Lets a drawing being placed on one chart notice taps on a
 * sibling chart (multi-chart layouts such as the scalper) and hand the cursor over to it.
 */
const touchDrawCharts = new Set<Chart>()

export function registerTouchDrawChart(chart: Chart) {
    touchDrawCharts.add(chart)
}

export function unregisterTouchDrawChart(chart: Chart) {
    touchDrawCharts.delete(chart)
}

/** Chart other than `exclude` whose pane contains `target`, with that pane's index. */
export function findTouchDrawChartPane(
    target: EventTarget | null,
    exclude: Chart
): { chart: Chart; paneIndex: number } | null {
    if (!(target instanceof Node)) return null
    for (const chart of touchDrawCharts) {
        if (chart === exclude || chart.destroyed || !chart.chartApi) continue
        if (!chart.container?.contains(target)) continue
        const panes = (getPaneElements(chart.chartApi) ?? []) as HTMLElement[]
        const paneIndex = panes.findIndex((p) => p.contains(target))
        return paneIndex >= 0 ? { chart, paneIndex } : null
    }
    return null
}

/**
 * Touch "cursor" drawing (mobile / embedded): instead of committing a point where the finger lands,
 * a crosshair cursor is shown; dragging anywhere on the pane moves the cursor relative to the finger
 * and a tap commits a point at the cursor. Mirrors the TradingView mobile flow
 * ("1 of 4 · Move cursor to start point" → "2 of 4 · Tap to set first point" → …).
 */
export type TouchDrawStep = {
    drawingId: string
    /** 1-based step index. Each point takes two steps: move the cursor, then tap. */
    step: number
    /** Undefined for open-ended tools (path / polyline) where the point count is not known up front. */
    totalSteps?: number
    message: string
}

/** Finger movement (CSS px) below which a touch counts as a tap rather than a cursor drag. */
export const TOUCH_DRAW_TAP_SLOP_PX = 8
/** Maximum touch duration (ms) for a tap. */
export const TOUCH_DRAW_TAP_MAX_MS = 400

/** Number of points each tool needs before it completes. Tools not listed are open-ended. */
const DRAWING_POINT_COUNTS: Partial<Record<DrawingTypes, number>> = {
    trendLine: 2,
    ray: 2,
    arrow: 2,
    extendedLine: 2,
    trendAngle: 2,
    infoLine: 2,
    simpleLine: 2,
    horizontalLine: 1,
    horizontalRay: 1,
    verticalLine: 1,
    crossLine: 1,
    parallelChannel: 3,
    flatTopBottom: 3,
    disjointChannel: 3,
    rectangle: 2,
    ellipse: 3,
    triangle: 3,
    circle: 2,
    rotatedRectangle: 3,
    fibonacci: 2,
    trendBasedFibonacci: 3,
    fibonacciCircles: 2,
    gannFan: 2,
    positionForecast: 1,
    dateRange: 2,
    priceRange: 2,
    forecast: 2,
    xabcdPattern: 5,
    abcdPattern: 4,
    trianglePattern: 4,
    '3DivesPattern': 7,
    elliottImpulseWave: 6,
    headAndShoulderPattern: 7,
    text: 1,
    anchoredText: 1,
    priceNote: 2
}

export function getDrawingPointCount(type: DrawingTypes): number | undefined {
    return DRAWING_POINT_COUNTS[type]
}

/**
 * Step descriptor for the touch-draw banner.
 * @param pointsPlaced points committed so far
 * @param cursorMoved whether the cursor has been dragged since the last commit (the "tap" half of the step)
 */
export function getTouchDrawStep(
    drawingId: string,
    type: DrawingTypes,
    pointsPlaced: number,
    cursorMoved: boolean
): TouchDrawStep {
    const total = getDrawingPointCount(type)
    const step = pointsPlaced * 2 + (cursorMoved ? 2 : 1)
    const totalSteps = total !== undefined ? total * 2 : undefined

    let message: string
    if (total === undefined && pointsPlaced >= 2) {
        // Open-ended tools: any further tap adds a point; tapping the last point finishes.
        message = cursorMoved ? 'Tap to add point · tap last point to finish' : 'Move cursor to next point'
    } else if (!cursorMoved) {
        message = pointsPlaced === 0 ? 'Move cursor to start point' : 'Move cursor to next point'
    } else if (total !== undefined && pointsPlaced === total - 1) {
        message = 'Tap to finish up'
    } else {
        message = pointsPlaced === 0 ? 'Tap to set first point' : 'Tap to set next point'
    }

    return { drawingId, step, totalSteps, message }
}
